import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import type { DashboardOverviewAnalyticsResponse, DashboardTrendPoint } from '@/lib/api/dashboard';

import monthlyRevenueTrendIcon from '@/assets/images/dashboard-monthly-revenue-trend.svg';
import totalAppointmentsIcon from '@/assets/images/dashboard-total-appointments.svg';
import usersGroupRoundedNewIcon from '@/assets/images/users-group-rounded-new.svg';

import { CS_PRIMARY } from './dashboardHomeTokens';
import { formatDashboardCount, formatDashboardCurrency } from './dashboardHomeFormatters';

const InvoicesDueShieldIcon = () => (
  <Box
    component="svg"
    width={(20 * 18) / 20}
    height={20}
    viewBox="0 0 18 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    sx={{ display: 'block' }}
  >
    <path
      d="M0 8.41666C0 5.21907 0 3.62028 0.377516 3.08241C0.755031 2.54454 2.25832 2.02996 5.26491 1.00079L5.83772 0.804717C7.40498 0.268239 8.18861 0 9 0C9.81139 0 10.595 0.268239 12.1623 0.804717L12.7351 1.00079C15.7417 2.02996 17.245 2.54454 17.6225 3.08241C18 3.62028 18 5.21907 18 8.41666V9.99136C18 15.6294 13.761 18.3655 11.1014 19.5273C10.38 19.8424 10.0193 20 9 20C7.98074 20 7.62001 19.8424 6.89856 19.5273C4.23896 18.3655 0 15.6294 0 9.99136V8.41666Z"
      fill="#ff9f43"
    />
    <rect x={8.2} y={5.2} width={1.6} height={7.6} rx={0.8} fill="#ea5455" />
    <circle cx={9} cy={14.6} r={1} fill="#ea5455" />
  </Box>
);

const cardSx = {
  borderRadius: '16px',
  border: '1px solid #ebebeb',
  p: '17px',
  flex: 1,
  minWidth: { xs: '100%', sm: 160 },
  minHeight: 152,
};

export interface DashboardAnalyticsOverviewCardsProps {
  data?: DashboardOverviewAnalyticsResponse;
  isLoading?: boolean;
}

const asLoadingValue = (value: string, data?: DashboardOverviewAnalyticsResponse, isLoading?: boolean) =>
  isLoading && !data ? '--' : value;

const formatSignedCount = (value: number): string => `${value >= 0 ? '+' : ''}${formatDashboardCount(value)}`;
const formatSignedPercent = (value: number): string => `${value >= 0 ? '+' : ''}${value}%`;

type TrendDirection = 'up' | 'down';

const buildSparklinePath = (values: number[], width: number, height: number, padding = 1) => {
  if (values.length === 0) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const xStep = values.length > 1 ? (width - padding * 2) / (values.length - 1) : 0;
  return values
    .map((value, index) => {
      const x = padding + xStep * index;
      const y = height - padding - ((value - min) / range) * (height - padding * 2);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const buildSparklineAreaPath = (linePath: string, pointCount: number, width: number, height: number, padding = 1) => {
  if (!linePath) return '';
  const endX = pointCount > 1 ? width - padding : padding;
  return `${linePath} L${endX.toFixed(2)} ${height} L${padding} ${height} Z`;
};

interface DashboardAnalyticsSparklineProps {
  points?: DashboardTrendPoint[];
  color: string;
}

const DashboardAnalyticsSparkline: React.FC<DashboardAnalyticsSparklineProps> = ({ points, color }) => {
  const gradientId = React.useId().replace(/:/g, '');
  const values = (points ?? [])
    .map((point) => Number(point.value))
    .filter((value) => Number.isFinite(value));
  const chartValues = values.length > 0 ? values : [0, 0];
  const width = 84;
  const height = 40;
  const linePath = buildSparklinePath(chartValues, width, height);
  const areaPath = buildSparklineAreaPath(linePath, chartValues.length, width, height);

  return (
    <Box
      component="svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      sx={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2={height} gradientUnits="userSpaceOnUse">
          <stop stopColor={color} stopOpacity="0.3" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
};

interface CardTrendIndicatorProps {
  color: string;
  direction: TrendDirection;
  value: string;
  label: string;
}

const CardTrendValue: React.FC<Omit<CardTrendIndicatorProps, 'label'>> = ({ color, direction, value }) => {
  const Icon = direction === 'down' ? ArrowDownwardRoundedIcon : ArrowUpwardRoundedIcon;

  return (
    <Stack direction="row" alignItems="center" spacing={0.35} sx={{ color }}>
      <Icon sx={{ fontSize: 16 }} />
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
        {value}
      </Typography>
    </Stack>
  );
};

interface CardMetricRowProps {
  label: string;
  value: string;
  trend?: CardTrendIndicatorProps;
  helperText?: string;
}

const CardMetricRow: React.FC<CardMetricRowProps> = ({ label, value, trend, helperText }) => (
  <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1.5} sx={{ minWidth: 0 }}>
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: '#000' }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color: '#111', letterSpacing: 0, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {trend ? (
        <CardTrendValue color={trend.color} direction={trend.direction} value={trend.value} />
      ) : helperText ? (
        <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.15, color: '#4b5563' }}>
          {helperText}
        </Typography>
      ) : null}
    </Stack>
    {trend ? (
      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          maxWidth: 104,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.15,
          color: '#4b5563',
          textAlign: 'right',
        }}
      >
        {trend.label}
      </Typography>
    ) : null}
  </Stack>
);

export const DashboardAnalyticsOverviewCards: React.FC<DashboardAnalyticsOverviewCardsProps> = ({
  data,
  isLoading = false,
}) => {
  const cards = data?.cards;
  const revenueCurrency = cards?.monthlyRevenue.currency;
  const revenueDelta = cards?.monthlyRevenue.deltaPercent ?? 0;
  const newClients = cards?.totalClients.newThisMonth ?? 0;
  const clientDelta = cards?.totalClients.deltaNewClients ?? newClients;
  const isCustomRange = Boolean(data?.period.isCustomRange);
  const rangeNoun = isCustomRange ? 'range' : '30 days';
  const newClientsPeriodLabel = isCustomRange ? 'In range' : 'Last 30 days';
  const appointmentWindowDays = cards?.totalAppointments.windowDays ?? 7;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} useFlexGap sx={{ width: '100%', alignItems: 'stretch' }}>
      <Paper elevation={0} sx={{ ...cardSx, width: '100%', minWidth: { xs: 0, sm: 160 }, maxWidth: { xs: 560, md: 'none' } }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '14px',
              bgcolor: 'rgba(32, 174, 220, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box component="img" src={usersGroupRoundedNewIcon} alt="" aria-hidden sx={{ width: 20, height: 20, objectFit: 'contain', display: 'block' }} />
          </Box>
          <DashboardAnalyticsSparkline points={cards?.totalClients.trendPoints} color={clientDelta >= 0 ? CS_PRIMARY : '#ea5455'} />
        </Stack>
        <CardMetricRow
          label="Total Clients"
          value={asLoadingValue(formatDashboardCount(cards?.totalClients.count), data, isLoading)}
          trend={{
            color: clientDelta >= 0 ? CS_PRIMARY : '#ea5455',
            direction: clientDelta >= 0 ? 'up' : 'down',
            value: asLoadingValue(formatSignedCount(newClients), data, isLoading),
            label: newClientsPeriodLabel,
          }}
        />
      </Stack>
      </Paper>

      <Paper elevation={0} sx={cardSx}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '14px',
              bgcolor: 'rgba(0, 187, 167, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box component="img" src={monthlyRevenueTrendIcon} alt="" aria-hidden sx={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }} />
          </Box>
          <DashboardAnalyticsSparkline points={cards?.monthlyRevenue.trendPoints} color={revenueDelta >= 0 ? '#00bba7' : '#ea5455'} />
        </Stack>
        <CardMetricRow
          label="Monthly Revenue"
          value={asLoadingValue(formatDashboardCurrency(cards?.monthlyRevenue.amount, revenueCurrency), data, isLoading)}
          trend={{
            color: revenueDelta >= 0 ? '#00bba7' : '#ea5455',
            direction: revenueDelta >= 0 ? 'up' : 'down',
            value: asLoadingValue(formatSignedPercent(revenueDelta), data, isLoading),
            label: `vs prev ${rangeNoun}`,
          }}
        />
      </Stack>
      </Paper>

      <Paper elevation={0} sx={cardSx}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '14px',
              bgcolor: 'rgba(124, 92, 191, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box component="img" src={totalAppointmentsIcon} alt="" aria-hidden sx={{ width: 20, height: 20, objectFit: 'contain', display: 'block' }} />
          </Box>
        </Stack>
        <CardMetricRow
          label="Total Appointments"
          value={asLoadingValue(formatDashboardCount(cards?.totalAppointments.count), data, isLoading)}
          helperText={`Next ${appointmentWindowDays} days`}
        />
      </Stack>
      </Paper>

      <Paper
      elevation={0}
      sx={{
        ...cardSx,
        minHeight: 152,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '14px',
              bgcolor: 'rgba(255, 159, 67, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InvoicesDueShieldIcon />
          </Box>
        </Stack>
        <CardMetricRow
          label="Invoices Due"
          value={asLoadingValue(formatDashboardCount(cards?.invoicesDue.count), data, isLoading)}
          helperText="Overdue today"
        />
      </Stack>
      </Paper>
    </Stack>
  );
};
